import React from 'react'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import LandlordHeader from '../../LandlordHeader'
import { useAppSelector } from '../../../../hooks/redux'
import { type RootState } from '../../../../store/store'
import ActionCard from '../actionCard'
import { mapPortalBitrixRequests } from '../../../../functions/shared/orders/portalBitrixRequests'

export default function PortalRequestActions(): JSX.Element {
    const { orderID } = useParams()
    const navigate = useNavigate()

    const allUsers = useAppSelector((state: RootState) => state.user.allUsers)
    const units = useAppSelector((state: RootState) => state.units.units)

    const request = useMemo(() => {
        const allRequests = mapPortalBitrixRequests(allUsers as any[], units as any[])
        return allRequests.find((item) => item.orderId === Number(orderID))
    }, [allUsers, units, orderID])

    if (request == null) {
        return (
            <>
                <LandlordHeader active={'Portal request'} />
                <div className={'d-flex flex-column gap-4 p-4 mt-14'}>
                    <div className="card shadow-sm p-4">
                        <h4 className="mb-2">Request not found</h4>
                        <div className="text-gray-700">This Bitrix portal request is not available in the current admin cache.</div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <LandlordHeader active={'Portal request'} />
            <div className={'d-flex flex-column gap-4 p-4 mt-14'}>
                <div className="card shadow-sm p-4">
                    <h4 className="mb-3">{request.requestType === 'viewing' ? 'Viewing Request' : 'Offer Request'} (Bitrix Order #{request.orderId})</h4>
                    <div className="d-flex flex-column gap-2 fs-6">
                        <div><b>Status:</b> {request.statusLabel}</div>
                        <div><b>Created:</b> {dayjs(request.createdAt).isValid() ? dayjs(request.createdAt).format('DD MMM YYYY, HH:mm') : request.createdAt}</div>
                        <div><b>Client:</b> {request.requesterName}</div>
                        <div><b>Email:</b> {request.requesterEmail}</div>
                        <div><b>Phone:</b> {request.requesterPhone}</div>
                        <div><b>Unit:</b> {request.unitLabel}</div>
                        <div><b>Property:</b> {request.propertyLabel}</div>
                        <div><b>Preferred viewing time:</b> {request.preferredTime}</div>
                        <div><b>Offer amount:</b> {request.offerAmount}</div>
                        <div><b>Comments:</b> {request.comments}</div>
                    </div>
                </div>

                {request.metadataRows.length > 0 &&
                    <div className="card shadow-sm p-4">
                        <h5 className="mb-3">Request metadata</h5>
                        <div className="table-responsive">
                            <table className="table align-middle table-row-dashed fs-6 gy-3 mb-0">
                                <tbody className="fw-semibold text-gray-800">
                                    {request.metadataRows.map((row) => (
                                        <tr key={`${row.label}-${row.value}`}>
                                            <td className="text-gray-600 text-uppercase fs-8">{row.label}</td>
                                            <td>{row.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>}

                {request.unitId != null &&
                    <ActionCard
                        title={'Open unit details'}
                        description={'Open unit driver/details panel to review related property context'}
                        onClick={() => {
                            navigate(`/landlord/modalView/unit_modal/${String(request.unitId)}/${String(request.orderId)}/calendar`)
                        }}
                        icon={'door_open'}
                    />}
            </div>
        </>
    )
}
